import React, { useState } from 'react';
import { Checkbox, colors } from '@insurancemenu/imenu-components-lib';
import { Mail, Reload, Delete } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { useTriggerRenewalsPageStore } from 'store/pages/tools/trigger-renewals/pageStore';
import { toJS } from 'mobx';
import BatchGuidelines from 'components/common/general/BatchGuidelines';
import BatchWarning from 'components/common/general/BatchWarning';
import SendOutEmailsModal from 'components/common/modals/SendOutEmailsModal';
import { GENERATE_FILES_FOR_AUTO_RENEW_FORM_ID } from 'components/tools/trigger-renewals/constants';
import { celticsRights, imCelticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import {
  BatchButtonsStyled,
  BatchSectionStyled,
  ButtonStyled,
  CheckboxTextStyled,
  SelectedAllBlockStyled,
  SectionTitleStyled,
} from './styles';

const TriggerRenewalsBatchSection: React.FC = observer(() => {
  const {
    userStore: { userRoles },
  } = useRootStore();
  const {
    batchActionDelegate: {
      isSelectAllButtonChecked,
      selectedPeriods,
      setSelectAllButtonStatus,
      openWarningModal,
      selectedPeriodsCanBeRenewed,
    },
    generateFilesForAutoRenewModalDelegate,
    isInitialLoading,
    isLoading,
  } = useTriggerRenewalsPageStore();

  const [isGuidelineModalOpen, setIsGuidelineModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const convertedRoles = userRoles.map((role) => toJS(role)).slice();

  const selectedPeriodsCount = selectedPeriods.length;
  const showGuidelineModal = selectedPeriodsCount === 0;

  const updateBatchModalState = (shouldWarningModalBeShown = true): void => {
    if (showGuidelineModal) {
      setIsWarningModalOpen(false);
      setIsGuidelineModalOpen(true);
    }
    if (!selectedPeriodsCanBeRenewed && shouldWarningModalBeShown) {
      setIsGuidelineModalOpen(false);
      setIsWarningModalOpen(true);
    }
  };

  return (
    <BatchSectionStyled>
      <SectionTitleStyled>Batch Apply Section</SectionTitleStyled>
      <BatchButtonsStyled>
        <SelectedAllBlockStyled>
          <Checkbox
            label={<CheckboxTextStyled>Select All Per page</CheckboxTextStyled>}
            checked={isSelectAllButtonChecked}
            onChange={setSelectAllButtonStatus}
            highlightColor={colors.blue[600]}
            disabled={isLoading || isInitialLoading}
          />
        </SelectedAllBlockStyled>
        <ButtonStyled
          leftIconProp={<Mail />}
          type="text"
          color="primary"
          disabled={isLoading || isInitialLoading}
          onClick={(): void => {
            updateBatchModalState();
            if (selectedPeriodsCanBeRenewed && selectedPeriodsCount !== 0) {
              openWarningModal('TRIGGER_SELECTED');
            }
          }}
        >
          Trigger
        </ButtonStyled>
        <BatchGuidelines
          show={isGuidelineModalOpen}
          alignment="center"
          onClose={(): void => {
            setIsGuidelineModalOpen(false);
          }}
          title="Select periods using checkboxes to utilize the batch feature"
        />
        <BatchWarning
          show={isWarningModalOpen}
          alignment="center"
          onClose={(): void => {
            setIsWarningModalOpen(false);
          }}
          title="Select periods that include exhibits and product rates to use this action"
        />
        {celticsRights(convertedRoles) && (
          <ButtonStyled
            leftIconProp={<Reload />}
            type="text"
            color="primary"
            disabled={isLoading || isInitialLoading}
            onClick={(): void => {
              updateBatchModalState();
              if (selectedPeriodsCanBeRenewed) {
                openWarningModal('MOVE_SELECTED_PERIODS_TO_RENEWAL');
              }
            }}
          >
            Move to renewal
          </ButtonStyled>
        )}
        {imCelticsRights(convertedRoles) && (
          <ButtonStyled
            leftIconProp={<Delete />}
            type="text"
            color="custom"
            customColor={colors.red[600]}
            disabled={isLoading || isInitialLoading}
            onClick={(): void => {
              updateBatchModalState(false);
              if (selectedPeriodsCount !== 0) {
                openWarningModal('REMOVE_PERIODS');
              }
            }}
          >
            Remove Periods
          </ButtonStyled>
        )}
      </BatchButtonsStyled>
      <SendOutEmailsModal
        title="Generate Auto-Renew Files"
        subtitle="Enter your email and we’ll send you the files once they are ready."
        modalStore={generateFilesForAutoRenewModalDelegate}
        formId={GENERATE_FILES_FOR_AUTO_RENEW_FORM_ID}
      />
    </BatchSectionStyled>
  );
});

export default TriggerRenewalsBatchSection;
